import { onBeforeUnmount, ref, type Ref } from 'vue'

import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { useAnalytics } from '@/utils/useAnalytics'

interface UseImageGenerationParams {
  menuItemId: Ref<string>
  brandId: Ref<string>
  parentMediaUrl: Ref<string | null>
  itemName: Ref<string>
  itemDescription: Ref<string | null>
  flowId?: Ref<number>
  analysisResults?: Ref<Record<string, string> | null>
  attachmentUrls?: Ref<string[]>
}

interface PendingGeneration {
  id: string
  status: 'pending' | 'failed'
  failedAt?: Date
  runId?: string
}

interface UseImageGenerationReturn {
  pendingGenerations: Ref<PendingGeneration[]>
  isGenerating: Ref<boolean>
  startGeneration: () => Promise<void>
  retryGeneration: (id: string) => Promise<void>
  removePendingGeneration: (id: string) => void
}

const CONDUCTOR_API_URL =
  'https://conductor-production-662c.up.railway.app/runs.json'
const DEFAULT_FLOW_ID = 34
const POLL_INTERVAL = 1000
const MAX_POLL_DURATION = 120000

/**
 * Composable for managing image generation workflow via Conductor API.
 * Supports multiple generation modes:
 * - Analyze mode (flowId=34): Pass analysisResults for style analysis
 * - Restage mode (flowId=36): Pass attachmentUrls for vanguard reference images
 * - Future modes can combine both analysisResults and attachmentUrls
 * @param params - Configuration parameters for image generation
 */
export function useImageGeneration(
  params: UseImageGenerationParams
): UseImageGenerationReturn {
  const { track } = useAnalytics()
  const pendingGenerations = ref<PendingGeneration[]>([])
  const isGenerating = ref(false)
  const pollIntervals = new Map<string, NodeJS.Timeout>()
  const autoRemoveTimeouts = new Map<string, NodeJS.Timeout>()
  const generationStartTimes = new Map<string, number>()

  onBeforeUnmount(() => {
    pollIntervals.forEach((interval) => clearInterval(interval))
    pollIntervals.clear()
    autoRemoveTimeouts.forEach((timeout) => clearTimeout(timeout))
    autoRemoveTimeouts.clear()
  })

  const callConductorAPI = async (): Promise<{ runId: string }> => {
    if (!params.parentMediaUrl.value) {
      throw new Error('Parent image URL is required for generation')
    }

    const flowId = params.flowId?.value ?? DEFAULT_FLOW_ID

    const run: Record<string, unknown> = {
      flow_id: flowId,
      input_image_url: params.parentMediaUrl.value,
    }

    if (params.analysisResults?.value) {
      run.variables = {
        problemReport: {
          description: params.itemDescription.value ?? undefined,
          imageUrl: params.parentMediaUrl.value,
          name: params.itemName.value,
          ...params.analysisResults.value,
        },
      }
    } else {
      run.variables = {
        description: params.itemDescription.value ?? undefined,
        name: params.itemName.value,
      }
    }

    if (
      params.attachmentUrls?.value &&
      params.attachmentUrls.value.length > 0
    ) {
      run.attachment_urls = params.attachmentUrls.value
    }

    const response = await fetch(CONDUCTOR_API_URL, {
      body: JSON.stringify({ run }),
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    return { runId: data.id || data.run_id || data.runId }
  }

  const fetchRunStatus = async (
    runId: string
  ): Promise<{ url?: string; status: string }> => {
    const response = await fetch(
      `https://conductor-production-662c.up.railway.app/runs/${runId}.json`,
      {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
        method: 'GET',
      }
    )

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    const status = data.status

    if (status !== 'completed') {
      return { status }
    }

    const url = data.data.image_url

    return { status, url }
  }

  const saveGeneratedImage = async (imageUrl: string): Promise<void> => {
    const mediaResponse = await OlympusClient.media.v1.createMedia({
      body: {
        brandId: params.brandId.value,
        mimeType: 'image/jpeg',
        source: 'ai-generated',
        url: imageUrl,
      },
    })

    await OlympusClient.media.v1.menuItem.menuItem.attachMedia({
      body: { mediaId: mediaResponse.media.id, purpose: 'suggested_image' },
      params: { itemId: params.menuItemId.value },
    })
  }

  const startAutoRemoveTimer = (id: string): void => {
    const timeout = setTimeout(() => {
      removePendingGeneration(id)
      autoRemoveTimeouts.delete(id)
    }, 30000)
    autoRemoveTimeouts.set(id, timeout)
  }

  const pollForCompletion = async (
    id: string,
    runId: string
  ): Promise<void> => {
    const startTime = Date.now()

    return await new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const elapsed = Date.now() - startTime

          if (elapsed > MAX_POLL_DURATION) {
            clearInterval(interval)
            pollIntervals.delete(id)
            const generation = pendingGenerations.value.find(
              (gen) => gen.id === id
            )
            if (generation) {
              generation.status = 'failed'
              generation.failedAt = new Date()
              startAutoRemoveTimer(id)
            }

            track('image_generation_failed', {
              error: 'timeout',
              mediaId: id,
              menuItemId: params.menuItemId.value,
            })
            generationStartTimes.delete(id)

            reject(
              new Error(
                `Image generation timed out after ${MAX_POLL_DURATION / 1000} seconds`
              )
            )
            return
          }

          const result = await fetchRunStatus(runId)

          if (result.status === 'completed' && result.url) {
            clearInterval(interval)
            pollIntervals.delete(id)

            try {
              await saveGeneratedImage(result.url)
            } catch (saveError) {
              logError(saveError)
              const generation = pendingGenerations.value.find(
                (gen) => gen.id === id
              )
              if (generation) {
                generation.status = 'failed'
                generation.failedAt = new Date()
                startAutoRemoveTimer(id)
              }
              track('image_generation_failed', {
                error: 'save_failed',
                mediaId: id,
                menuItemId: params.menuItemId.value,
              })
              generationStartTimes.delete(id)
              reject(new Error('Failed to save generated image'))
              return
            }

            const generationStartTime = generationStartTimes.get(id)
            const durationMs = generationStartTime
              ? Date.now() - generationStartTime
              : undefined

            track('image_generation_completed', {
              durationMs,
              mediaId: id,
              menuItemId: params.menuItemId.value,
            })
            generationStartTimes.delete(id)

            removePendingGeneration(id)
            resolve()
          } else if (result.status === 'failed') {
            clearInterval(interval)
            pollIntervals.delete(id)
            const generation = pendingGenerations.value.find(
              (gen) => gen.id === id
            )
            if (generation) {
              generation.status = 'failed'
              generation.failedAt = new Date()
              startAutoRemoveTimer(id)
            }

            track('image_generation_failed', {
              error: 'generation_failed',
              mediaId: id,
              menuItemId: params.menuItemId.value,
            })
            generationStartTimes.delete(id)

            reject(new Error('Image generation failed'))
          }
        } catch (error) {
          clearInterval(interval)
          pollIntervals.delete(id)
          const generation = pendingGenerations.value.find(
            (gen) => gen.id === id
          )
          if (generation) {
            generation.status = 'failed'
            generation.failedAt = new Date()
            startAutoRemoveTimer(id)
          }

          track('image_generation_failed', {
            error: error instanceof Error ? error.message : 'unknown',
            mediaId: id,
            menuItemId: params.menuItemId.value,
          })
          generationStartTimes.delete(id)

          reject(error)
        }
      }, POLL_INTERVAL)

      pollIntervals.set(id, interval)
    })
  }

  const startGeneration = async (): Promise<void> => {
    if (isGenerating.value) {
      return
    }

    const id = `pending-${Date.now()}`
    const generation: PendingGeneration = {
      id,
      status: 'pending',
    }

    generationStartTimes.set(id, Date.now())
    track('image_generation_started', {
      mediaId: id,
      menuItemId: params.menuItemId.value,
    })

    pendingGenerations.value.push(generation)
    isGenerating.value = true

    try {
      const { runId } = await callConductorAPI()
      generation.runId = runId
      await pollForCompletion(id, runId)
    } catch (error) {
      logError(error)
    } finally {
      isGenerating.value = false
    }
  }

  const retryGeneration = async (id: string): Promise<void> => {
    const generation = pendingGenerations.value.find((gen) => gen.id === id)
    if (!generation || generation.status !== 'failed') {
      return
    }

    const existingTimeout = autoRemoveTimeouts.get(id)
    if (existingTimeout) {
      clearTimeout(existingTimeout)
      autoRemoveTimeouts.delete(id)
    }

    track('image_generation_retried', {
      mediaId: id,
      menuItemId: params.menuItemId.value,
    })

    generation.status = 'pending'
    generation.failedAt = undefined
    isGenerating.value = true
    generationStartTimes.set(id, Date.now())

    try {
      const { runId } = await callConductorAPI()
      generation.runId = runId
      await pollForCompletion(id, runId)
    } catch (error) {
      logError(error)
    } finally {
      isGenerating.value = false
    }
  }

  const removePendingGeneration = (id: string): void => {
    const existingInterval = pollIntervals.get(id)
    if (existingInterval) {
      clearInterval(existingInterval)
      pollIntervals.delete(id)
    }

    const existingTimeout = autoRemoveTimeouts.get(id)
    if (existingTimeout) {
      clearTimeout(existingTimeout)
      autoRemoveTimeouts.delete(id)
    }

    pendingGenerations.value = pendingGenerations.value.filter(
      (gen) => gen.id !== id
    )
  }

  return {
    isGenerating,
    pendingGenerations,
    removePendingGeneration,
    retryGeneration,
    startGeneration,
  }
}
